import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import { type CodeSyntaxValidation } from "@intelligems/ig-theme/IgCodeEditor";

import type {
  WorkspaceApiPort,
  WorkspaceImportedArtifacts,
  WorkspaceImportedDraft,
} from "../apiPort";
import type {
  WorkspaceBrainConfig,
  WorkspaceReadonlyFile,
} from "../domainConfig";
import { workspaceModelUri } from "../monaco/workspaceMonacoModels";
import {
  inventoryLocations,
  scanSuiteTestLocations,
  suiteGateVerdicts,
} from "../testVerdicts";
import type {
  PublishBlockReason,
  WorkspaceConsoleGroup,
  WorkspaceEditorReveal,
  WorkspaceEditorState,
  WorkspaceEditorTestFiles,
  WorkspaceGateResult,
  WorkspaceLogEntry,
  WorkspaceViewModel,
} from "../types";

// ---------------------------------------------------------------------------
// The shared code-workspace brain.
//
// Every server call goes through the injected {@link WorkspaceApiPort}; every
// label, filename, gate and confirm string comes from the injected
// {@link WorkspaceBrainConfig}. There is no domain branch in this file.
//
// NOTE ON `useMemo`/`useCallback`: the project style guide discourages them
// under the React Compiler, but the ones here are effect dependencies —
// changing their identity semantics changes *when* effects fire, which is
// behavior, not performance. They were moved verbatim and must stay that way
// until they are removed deliberately, with the effects re-verified.
// ---------------------------------------------------------------------------

/**
 * Merges the canonical ordered gate list with the live results from the active
 * publish job. Gates not yet reported by the job render as `pending`.
 */
const mergeGates = <GateKey extends string>(
  order: readonly GateKey[],
  liveGates: WorkspaceGateResult<GateKey>[] | undefined
): WorkspaceGateResult<GateKey>[] => {
  const byKey = new Map((liveGates ?? []).map((g) => [g.gateKey, g]));
  return order.map((gateKey): WorkspaceGateResult<GateKey> => {
    const gate = byKey.get(gateKey);
    if (gate === undefined) {
      return {
        gateKey,
        status: "pending",
        result: null,
        detailsJson: null,
        startedAtTs: null,
        finishedAtTs: null,
      };
    }

    return {
      ...gate,
      status: normalizeGateStatus(gate.status),
    };
  });
};

const withContractRunning = <GateKey extends string>(
  gates: WorkspaceGateResult<GateKey>[],
  contractGate: GateKey,
  loading: boolean
): WorkspaceGateResult<GateKey>[] =>
  gates.map((gate) =>
    gate.gateKey === contractGate && loading
      ? {
          ...gate,
          status: "running",
          result: null,
          startedAtTs: null,
          finishedAtTs: null,
        }
      : gate
  );

const isValidGateStatus = (
  value: unknown
): value is WorkspaceGateResult["status"] =>
  value === "pending" ||
  value === "running" ||
  value === "passed" ||
  value === "failed" ||
  value === "warning" ||
  value === "blocked";

const normalizeGateStatus = (status: unknown): WorkspaceGateResult["status"] =>
  isValidGateStatus(status) ? status : "pending";

const isLogEntry = (value: unknown): value is WorkspaceLogEntry => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const log = value as Record<string, unknown>;
  return (
    ["log", "info", "warn", "error", "debug"].includes(String(log.level)) &&
    typeof log.message === "string" &&
    typeof log.timestamp === "string"
  );
};

const isLogEntryArray = (value: unknown): value is WorkspaceLogEntry[] =>
  Array.isArray(value) && value.every(isLogEntry);

/** Narrows one entry of a gate's `detailsJson.consoleLogs` to a console group. */
const isConsoleGroup = (value: unknown): value is WorkspaceConsoleGroup => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const group = value as Record<string, unknown>;
  return (
    (group.testName === null || typeof group.testName === "string") &&
    isLogEntryArray(group.logs)
  );
};

/** Reads the grouped `consoleLogs` off a gate's `detailsJson`, or `[]`. */
const gateConsoleLogs = (
  gate: WorkspaceGateResult<string> | undefined
): WorkspaceConsoleGroup[] => {
  const raw = gate?.detailsJson?.consoleLogs;
  return Array.isArray(raw) ? raw.filter(isConsoleGroup) : [];
};

/** `true` when a gate reported at least one structured logger entry. */
const gateHasConsolePayload = (
  gate: WorkspaceGateResult<string> | undefined
): boolean => gateConsoleLogs(gate).some((group) => group.logs.length > 0);

type CachedGeneratedTestSuite = {
  suite: string;
  draftLastUpdateTs: number | null;
};

const parseGeneratedTestSuiteCache = (
  raw: string
): CachedGeneratedTestSuite | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<CachedGeneratedTestSuite>;
    if (typeof parsed.suite !== "string" || parsed.suite.length === 0) {
      return null;
    }

    const parsedDraftLastUpdateTs = parsed.draftLastUpdateTs;
    if (
      parsedDraftLastUpdateTs !== undefined &&
      parsedDraftLastUpdateTs !== null &&
      (typeof parsedDraftLastUpdateTs !== "number" ||
        !Number.isFinite(parsedDraftLastUpdateTs))
    ) {
      return null;
    }
    return {
      suite: parsed.suite,
      draftLastUpdateTs:
        parsedDraftLastUpdateTs === undefined ||
        parsedDraftLastUpdateTs === null
          ? null
          : parsedDraftLastUpdateTs,
    };
  } catch {
    return null;
  }
};

const fetchTextArtifact = async (
  url: string | null | undefined
): Promise<string | null> => {
  if (!url) {
    return null;
  }
  const resp = await fetch(url);
  if (!resp.ok) {
    return null;
  }
  const text = await resp.text();
  return text.trim().length > 0 ? text : null;
};

/** Shallow value-equality for two filename → content maps. */
const areFileMapsEqual = (
  a: Record<string, string>,
  b: Record<string, string>
): boolean => {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) {
    return false;
  }
  return aKeys.every((key) => b[key] === a[key]);
};

/**
 * The worker's verdict for one model, if it was given for exactly `source`;
 * `null` until the worker answers for the current source.
 */
const typecheckFor = (
  typechecks: Record<
    string,
    { source: string; validation: CodeSyntaxValidation }
  >,
  modelUri: string,
  source: string
): CodeSyntaxValidation | null => {
  const cached = typechecks[modelUri];
  return cached?.source === source ? cached.validation : null;
};

/** What stands in for a verdict that has not landed: "checking", never "invalid". */
const CHECKING_VALIDATION: CodeSyntaxValidation = {
  isValid: false,
  errorMessage: "Checking TypeScript...",
  errorLine: null,
  errorColumn: null,
  errorCount: 0,
};

/**
 * Wires an injected {@link WorkspaceApiPort} and {@link WorkspaceBrainConfig}
 * into the typed {@link WorkspaceViewModel} every workspace surface consumes.
 * Complex, multi-field local state (editor buffer, selections, modal flags) is
 * held in a single immer-backed object.
 */
export const useCodegenWorkspace = <
  GateKey extends string,
  TabKey extends string,
>(
  config: WorkspaceBrainConfig<GateKey>,
  port: WorkspaceApiPort<GateKey>,
  defaultTab: TabKey
): WorkspaceViewModel<GateKey, TabKey> => {
  const entityRef = port.entityRef;
  const hasEntityRef = !!entityRef;
  const { entryFilename, typesFilename, readonlyFiles } = config.files;
  const gateOrder = useMemo(
    () => config.gates.order.map((spec) => spec.key),
    [config.gates.order]
  );

  const draft = port.draft;
  const activeVersion = port.activeVersion;

  // Always holds the latest `draft` so async fetch effects can read the current
  // `lastUpdateTs` at call time without capturing a stale value in their closure
  // (and without putting `draft` in their dep arrays, which would re-trigger the
  // fetch on every unrelated draft change). Updated synchronously on each render.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // The persisted draft bytes are decoded by the domain codec. `savedFiles` is
  // the last-persisted editable file set; a brand-new entity seeds the entry
  // from the domain's starter source.
  const decodedFiles = useMemo<Record<string, string>>(
    () => config.files.decode(port.source.data ?? ""),
    [port.source.data, config.files]
  );
  const savedFiles = useMemo<Record<string, string>>(
    () =>
      draft ? decodedFiles : { [entryFilename]: config.files.starterSource },
    [draft, decodedFiles, entryFilename, config.files.starterSource]
  );

  // --- local editor state (immer-backed) ------------------------------------
  // `filesOverride` is the live editable file set once the user has touched the
  // workspace; until then we mirror the saved files so a freshly-loaded draft
  // shows its persisted code. `activeFile` is the open tab (an editable filename
  // or the readonly contract module).
  const [local, setLocal] = useImmer<{
    filesOverride: Record<string, string> | null;
    activeFile: string;
    generatedTestSuite: string | null;
    savedGeneratedTestSuite: string | null;
    userTestSuite: string | null;
    savedUserTestSuite: string | null;
    skipAiTests: boolean;
    reveal: WorkspaceEditorReveal | null;
    adoptingTestId: string | null;
  }>({
    filesOverride: null,
    activeFile: entryFilename,
    generatedTestSuite: null,
    savedGeneratedTestSuite: null,
    userTestSuite: null,
    savedUserTestSuite: null,
    skipAiTests: false,
    reveal: null,
    adoptingTestId: null,
  });
  const previousSavedFiles = useRef(savedFiles);
  useEffect(() => {
    const previous = previousSavedFiles.current;
    previousSavedFiles.current = savedFiles;
    setLocal((state) => {
      // A saved buffer follows later server edits. An unsaved buffer remains local.
      if (
        state.filesOverride &&
        (areFileMapsEqual(state.filesOverride, previous) ||
          areFileMapsEqual(state.filesOverride, savedFiles))
      ) {
        state.filesOverride = null;
      }
    });
  }, [savedFiles, setLocal]);

  // The presigned-URL + localStorage suite path predates suites-as-files. A
  // domain whose suites are editor files carries its generated suite on the
  // draft and inventories it server-side, so for it the whole legacy path is
  // off: nothing is fetched from `generatedTestsUrl`, and nothing is read
  // from or written to localStorage. Two sources of truth for one suite is
  // how a stale cached copy got shown over what the pipeline had written.
  const legacyGeneratedSuite = config.editorTests === null;
  const generatedTestSuiteCacheKey =
    legacyGeneratedSuite && entityRef && config.generatedTestsCachePrefix
      ? `${config.generatedTestsCachePrefix}:${entityRef}`
      : null;

  const clearGeneratedTestSuiteCache = useCallback(() => {
    if (!generatedTestSuiteCacheKey) {
      return;
    }
    try {
      localStorage.removeItem(generatedTestSuiteCacheKey);
    } catch {
      // Ignore cache failures (e.g. private browsing mode).
    }
  }, [generatedTestSuiteCacheKey]);

  const persistGeneratedTestSuite = useCallback(
    (suite: string, draftLastUpdateTs: number | null) => {
      if (!generatedTestSuiteCacheKey) {
        return;
      }
      try {
        localStorage.setItem(
          generatedTestSuiteCacheKey,
          JSON.stringify({
            suite,
            draftLastUpdateTs,
          })
        );
      } catch {
        // Ignore cache failures (e.g. private browsing mode).
      }
    },
    [generatedTestSuiteCacheKey]
  );

  // Single owner of the "generated suite is written → it is persisted" invariant.
  // Every path that populates `generatedTestSuite` (URL fetch, job result, manual
  // generate) commits through here so the in-memory value and the localStorage
  // cache can never drift apart. `draftLastUpdateTs` is supplied by the caller so
  // each site can tag the cache with the timestamp current at its own resolve
  // time (effects read the latest via `draftRef`). The committed value is also
  // the dirty baseline (`savedGeneratedTestSuite`): a freshly-loaded suite is
  // never dirty, and only a subsequent user edit (via `setGeneratedTestSuite`)
  // marks it dirty so the next save persists it.
  const commitGeneratedTestSuite = useCallback(
    (suite: string, draftLastUpdateTs: number | null) => {
      setLocal((state) => {
        state.generatedTestSuite = suite;
        state.savedGeneratedTestSuite = suite;
      });
      persistGeneratedTestSuite(suite, draftLastUpdateTs);
    },
    [persistGeneratedTestSuite, setLocal]
  );

  /**
   * A settle from the server or a job: the baseline always moves to `suite`,
   * the buffer follows only when it carries nothing unsaved. `commit` above
   * is the unconditional form, for a load into an empty buffer.
   */
  const seedGeneratedTestSuite = useCallback(
    (suite: string, draftLastUpdateTs: number | null) => {
      let followed = false;
      setLocal((state) => {
        const wasDirty =
          state.generatedTestSuite !== null &&
          state.generatedTestSuite !== (state.savedGeneratedTestSuite ?? "");
        state.savedGeneratedTestSuite = suite;
        if (!wasDirty) {
          state.generatedTestSuite = suite;
          followed = true;
        }
      });
      if (followed) {
        persistGeneratedTestSuite(suite, draftLastUpdateTs);
      }
    },
    [persistGeneratedTestSuite, setLocal]
  );

  const loadGeneratedTestSuiteFromCache = useCallback(() => {
    if (!generatedTestSuiteCacheKey) {
      return;
    }
    if (local.generatedTestSuite !== null) {
      return;
    }
    if (draft?.generatedTestsUrl) {
      return;
    }

    try {
      const raw = localStorage.getItem(generatedTestSuiteCacheKey);
      const cached = raw ? parseGeneratedTestSuiteCache(raw) : null;
      if (!cached) {
        return;
      }

      if (
        cached.draftLastUpdateTs !== null &&
        draft?.lastUpdateTs !== undefined &&
        cached.draftLastUpdateTs !== draft.lastUpdateTs
      ) {
        clearGeneratedTestSuiteCache();
        return;
      }

      setLocal((state) => {
        if (state.generatedTestSuite === null) {
          state.generatedTestSuite = cached.suite;
          state.savedGeneratedTestSuite = cached.suite;
        }
      });
    } catch {
      // Ignore cache failures.
    }
  }, [
    clearGeneratedTestSuiteCache,
    draft?.generatedTestsUrl,
    draft?.lastUpdateTs,
    generatedTestSuiteCacheKey,
    local.generatedTestSuite,
    setLocal,
  ]);

  const [selectedTab, setSelectedTab] = useImmer<TabKey>(defaultTab);

  // --- source provenance -----------------------------------------------------
  // What the next draft save should record as the source's origin. `null` until
  // the source buffer is touched this session, so a dependency-only save leaves
  // the stored provenance untouched. AI-chat applies set "chat"; any human edit
  // afterwards flips it back to "manual".
  const [sourceOrigin, setSourceOrigin] = useImmer<"chat" | "manual" | null>(
    null
  );

  // --- modal flags -----------------------------------------------------------
  const [isPublishModalOpen, setPublishModalOpen] = useImmer(false);
  const [rollbackTargetVersion, setRollbackTargetVersion] = useImmer<
    number | null
  >(null);
  const [isSecretsModalOpen, setSecretsModalOpen] = useImmer(false);

  // --- active publish job ----------------------------------------------------
  // The job started by the latest `Run tests` (dry-run). Distinguishes a
  // test-run job — which drives the editor console drawer — from a Publish job.
  const [runTestsJobId, setRunTestsJobId] = useImmer<string | null>(null);
  // The run-tests job whose console drawer the user dismissed; cleared on the
  // next `Run tests` so a fresh run re-opens the drawer.
  const [consoleDismissedJobId, setConsoleDismissedJobId] = useImmer<
    string | null
  >(null);
  // Gate rows to hold during the brief handoff from runGates mutation success
  // to the first progress frame for the new job.
  const [runGatesTransitionGates, setRunGatesTransitionGates] = useImmer<
    WorkspaceGateResult<GateKey>[] | null
  >(null);
  // Holds the latest runGates jobId while we wait for first live progress.
  const [runGatesTransitionJobId, setRunGatesTransitionJobId] = useImmer<
    string | null
  >(null);
  const [isRunGatesTransitioning, setIsRunGatesTransitioning] = useImmer(false);

  const activeJobId = port.publishJob.trackedJobId;

  // --- derived editor state --------------------------------------------------
  const isEmpty = !port.isLoading && !draft;

  // The live editable file set: the user's in-progress override, else the saved
  // files (or the starter for a brand-new entity). The environment-injected
  // contract module (and any other locked tab) never lives in this map.
  const files = local.filesOverride ?? savedFiles;

  const savedUserTestSuite = local.savedUserTestSuite ?? "";
  const userTestSuite = local.userTestSuite ?? savedUserTestSuite;
  const userTestSuiteDirty = userTestSuite !== savedUserTestSuite;

  // The suites as editor files: on only when the domain configured them AND
  // its port can inventory the generated suite — either alone is not enough
  // to show a generated file whose tests can be adopted.
  const editorTests =
    config.editorTests !== null && port.suiteInventory !== null
      ? { config: config.editorTests, inventory: port.suiteInventory }
      : null;
  const userTestFilename = editorTests?.config.files.userFilename ?? null;
  const generatedTestFilename =
    editorTests?.config.files.generatedFilename ?? null;
  const generatedInventory = editorTests?.inventory.generated.data;
  const generatedSuiteSource = generatedInventory?.suite ?? null;

  // The requested tab, resolved against the locked contract tab, any other
  // locked read-only tab, the test files, or the editable set — falling back
  // to the entry file for a name that matches none of those (e.g. a deleted
  // user file).
  const activeReadonlyFile: WorkspaceReadonlyFile | null =
    local.activeFile === typesFilename
      ? { filename: typesFilename, source: config.files.typesModuleSource }
      : (readonlyFiles.find((file) => file.filename === local.activeFile) ??
        null);
  const activeTestFile: WorkspaceEditorTestFiles["active"] =
    activeReadonlyFile !== null
      ? null
      : local.activeFile === userTestFilename
        ? "user"
        : local.activeFile === generatedTestFilename
          ? "generated"
          : null;
  const activeFile = activeReadonlyFile
    ? activeReadonlyFile.filename
    : activeTestFile !== null || local.activeFile in files
      ? local.activeFile
      : entryFilename;
  const isReadonlyTabActive = activeReadonlyFile !== null;

  // The active buffer shown in the editor: a locked tab's fixed content when
  // one is open, a suite when a test file is, otherwise the active editable
  // file. The generated suite shows the inventory's copy, because the test
  // positions the editor marks were computed against exactly that text.
  const editorValue = activeReadonlyFile
    ? activeReadonlyFile.source
    : activeTestFile === "user"
      ? userTestSuite
      : activeTestFile === "generated"
        ? (generatedSuiteSource ?? "")
        : (files[activeFile] ?? "");
  const savedEditorValue = activeReadonlyFile
    ? activeReadonlyFile.source
    : activeTestFile === "user"
      ? savedUserTestSuite
      : activeTestFile === "generated"
        ? (generatedSuiteSource ?? "")
        : (savedFiles[activeFile] ?? "");
  const [typechecks, setTypechecks] = useImmer<
    Record<string, { source: string; validation: CodeSyntaxValidation }>
  >({});
  const activeModelUri = workspaceModelUri(activeFile);

  // Any editable file differing from the last-saved set marks the workspace
  // dirty (independent of which tab is open).
  const filesDirty = !areFileMapsEqual(files, savedFiles);

  /**
   * A bare, traversal-free `*.ts` filename that does not collide with an
   * existing file or a reserved/environment module — the same safety rules the
   * save-body validator enforces server-side.
   */
  const isValidUserFilename = useCallback(
    (name: string, current: Record<string, string>): boolean =>
      name.endsWith(".ts") &&
      !name.includes("/") &&
      !name.includes("\\") &&
      !name.includes("..") &&
      !name.startsWith("/") &&
      name !== entryFilename &&
      !config.files.reservedFilenames.includes(name) &&
      !(name in current),
    [entryFilename, config.files.reservedFilenames]
  );

  /** Generates the next free `helper.ts` / `helper-2.ts` … name. */
  const nextHelperFilename = useCallback(
    (current: Record<string, string>): string => {
      const base = config.files.newFileBasename;
      if (isValidUserFilename(`${base}.ts`, current)) {
        return `${base}.ts`;
      }
      for (let index = 2; ; index += 1) {
        const candidate = `${base}-${index}.ts`;
        if (isValidUserFilename(candidate, current)) {
          return candidate;
        }
      }
    },
    [config.files.newFileBasename, isValidUserFilename]
  );

  // --- file-tab actions ------------------------------------------------------
  // The contract types module and any other locked read-only tab — never
  // editable, deletable, or renameable.
  const isReadonlyFilename = useCallback(
    (name: string): boolean =>
      name === typesFilename ||
      readonlyFiles.some((file) => file.filename === name),
    [typesFilename, readonlyFiles]
  );

  const setFileValue = useCallback(
    (name: string, value: string) => {
      setLocal((d) => {
        if (isReadonlyFilename(name)) {
          return;
        }
        d.filesOverride = { ...(d.filesOverride ?? savedFiles), [name]: value };
      });
    },
    [savedFiles, setLocal, isReadonlyFilename]
  );

  const setActiveFile = useCallback(
    (name: string) => {
      setLocal((d) => {
        d.activeFile = name;
        // A reveal belongs to the moment it was requested; reopening the file
        // later must not jump back to it.
        d.reveal = null;
      });
    },
    [setLocal]
  );

  const addFile = useCallback(() => {
    setLocal((d) => {
      const base = d.filesOverride ?? savedFiles;
      const name = nextHelperFilename(base);
      d.filesOverride = { ...base, [name]: config.files.newFileTemplate };
      d.activeFile = name;
    });
  }, [savedFiles, setLocal, nextHelperFilename, config.files.newFileTemplate]);

  const deleteFile = useCallback(
    (name: string) => {
      // The entry and any locked read-only tab are protected.
      if (name === entryFilename || isReadonlyFilename(name)) {
        return;
      }
      setLocal((d) => {
        const base = d.filesOverride ?? savedFiles;
        if (!(name in base)) {
          return;
        }
        const next = { ...base };
        delete next[name];
        d.filesOverride = next;
        if (d.activeFile === name) {
          d.activeFile = entryFilename;
        }
      });
    },
    [savedFiles, setLocal, entryFilename, isReadonlyFilename]
  );

  const renameFile = useCallback(
    (from: string, to: string) => {
      if (from === entryFilename || isReadonlyFilename(from)) {
        return;
      }
      setLocal((d) => {
        const base = d.filesOverride ?? savedFiles;
        if (!(from in base) || !isValidUserFilename(to, base)) {
          return;
        }
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(base)) {
          next[key === from ? to : key] = value;
        }
        d.filesOverride = next;
        if (d.activeFile === from) {
          d.activeFile = to;
        }
      });
    },
    [
      savedFiles,
      setLocal,
      entryFilename,
      isReadonlyFilename,
      isValidUserFilename,
    ]
  );

  /**
   * The editor's verdict comes from ONE source: Monaco's TypeScript worker,
   * which is the same compiler the publish gates run.
   *
   * A CodeMirror/Lezer pre-pass used to answer first and short-circuit this,
   * which made the weaker of the two parsers the gate. Lezer's TS grammar
   * cannot parse a type predicate in an arrow function's return position
   * (`xs.filter((i): i is T => ...)`) — ordinary TypeScript that tsc accepts —
   * so an author writing it was blocked from saving or publishing by a syntax
   * error that did not exist. Until the worker answers for the current source
   * the verdict is "checking", never "invalid".
   */
  const activeFileSyntax =
    typecheckFor(typechecks, activeModelUri, editorValue) ??
    CHECKING_VALIDATION;
  // What gates Save and Publish. On a test tab that is the entry file's
  // verdict for its CURRENT source (see `WorkspaceEditorState.syntax`): the
  // editor reports one for the entry companion on every diagnostics pass, so
  // it lands whether or not the entry tab was ever opened, and an entry that
  // moves under an open test tab — an agent save — is re-verified rather
  // than judged by a verdict for text that no longer exists.
  const entrySource = files[entryFilename] ?? "";
  const entryTypecheck =
    activeTestFile === null
      ? typecheckFor(typechecks, activeModelUri, editorValue)
      : typecheckFor(typechecks, workspaceModelUri(entryFilename), entrySource);
  const editorSyntax = entryTypecheck ?? CHECKING_VALIDATION;
  const syntaxPending = entryTypecheck === null;

  const setEditorTypecheck = useCallback(
    (validation: CodeSyntaxValidation, source: string, modelUri?: string) => {
      const uri = modelUri ?? activeModelUri;
      setTypechecks((d) => {
        d[uri] = { source, validation };
      });
    },
    [activeModelUri]
  );

  const dependencies = port.controls.dependencies;
  const controlsDirty = port.controls.isDirty;
  // The AI suite is dirty only once a suite is loaded (`!== null`) and the buffer
  // diverges from the last-persisted baseline — including the user clearing it to
  // empty, which persists as a delete on save.
  const generatedTestSuiteDirty =
    local.generatedTestSuite !== null &&
    local.generatedTestSuite !== (local.savedGeneratedTestSuite ?? "");

  const editorState = useMemo<Omit<WorkspaceEditorState, "testFiles">>(
    () => ({
      value: editorValue,
      savedValue: savedEditorValue,
      isDirty: filesDirty,
      isLoading: port.source.isLoading,
      lastSavedTs: draft?.lastUpdateTs ?? null,
      lineCount: editorValue.split("\n").length,
      syntax: editorSyntax,
      syntaxPending,
      activeFileSyntax,
      reveal: local.reveal,
      dependencies,
      onTypecheckChange: setEditorTypecheck,
      // --- multi-file workspace ---
      files,
      activeFile,
      entryFile: entryFilename,
      typesFile: typesFilename,
      readonlyFiles: readonlyFiles.map((file) => file.filename),
      isActiveFileReadOnly:
        isReadonlyTabActive || activeTestFile === "generated",
      allowUserFiles: config.files.allowUserFiles,
      setActiveFile,
      addFile,
      deleteFile,
      renameFile,
    }),
    [
      activeFile,
      activeFileSyntax,
      activeTestFile,
      addFile,
      deleteFile,
      dependencies,
      draft?.lastUpdateTs,
      editorSyntax,
      editorValue,
      files,
      filesDirty,
      isReadonlyTabActive,
      local.reveal,
      readonlyFiles,
      renameFile,
      savedEditorValue,
      setActiveFile,
      setEditorTypecheck,
      syntaxPending,
      port.source.isLoading,
      entryFilename,
      typesFilename,
      config.files.allowUserFiles,
    ]
  );

  // Workspace-level dirty: either the editor buffer or the controls differ from
  // the saved draft. Drives the Save-draft button and blocks publishing unsaved
  // changes.
  const isDirty =
    editorState.isDirty ||
    controlsDirty ||
    userTestSuiteDirty ||
    generatedTestSuiteDirty;

  // --- publish state ---------------------------------------------------------
  const isContractStarting =
    port.isPublishing || (port.gateRun?.isRunningGates ?? false);
  const isRunGatesInTransition =
    isRunGatesTransitioning &&
    (runGatesTransitionJobId === null
      ? true
      : !port.publishJob.hasProgressFrame ||
        port.publishJob.job?.id !== runGatesTransitionJobId);
  const selectedRunTransitionGates =
    isRunGatesInTransition && runGatesTransitionGates
      ? runGatesTransitionGates
      : undefined;
  const baseGates = useMemo(
    () =>
      isRunGatesInTransition
        ? mergeGates(
            gateOrder,
            selectedRunTransitionGates ?? port.publishJob.job?.gates
          )
        : mergeGates(gateOrder, port.publishJob.job?.gates),
    [
      isRunGatesInTransition,
      port.publishJob.job?.gates,
      selectedRunTransitionGates,
      gateOrder,
    ]
  );

  const gates = useMemo(
    () =>
      isRunGatesInTransition
        ? withContractRunning(baseGates, config.gates.contractGate, true)
        : withContractRunning(
            baseGates,
            config.gates.contractGate,
            isContractStarting
          ),
    [
      baseGates,
      isContractStarting,
      isRunGatesInTransition,
      config.gates.contractGate,
    ]
  );
  const unpassedGateCount = gates.filter((g) => g.status !== "passed").length;
  const jobStatus = port.publishJob.job?.status;
  const generatedSuiteGateKey = config.gates.generatedSuiteGate;
  const generatedTestSuiteFromJob = useMemo(() => {
    const generatedGate = gates.find(
      (gate) => gate.gateKey === generatedSuiteGateKey
    );
    const testSuite = generatedGate?.detailsJson?.testSuite;
    return typeof testSuite === "string" && testSuite.length > 0
      ? testSuite
      : null;
  }, [gates, generatedSuiteGateKey]);

  // --- editor console (latest Run tests suite logs) --------------------------
  // The console drawer surfaces output captured by the required-tests and suite
  // gates, but only for the job started by the latest `Run tests` — never a
  // Publish job. It opens once a gate reports a payload and stays dismissed for
  // that run until the next `Run tests`.
  const requiredTestsGate = gates.find(
    (gate) => gate.gateKey === config.gates.consoleGates.required
  );
  const generatedSuiteGate = gates.find(
    (gate) => gate.gateKey === config.gates.consoleGates.generatedSuite
  );
  const userSuiteGate = gates.find(
    (gate) => gate.gateKey === config.gates.consoleGates.userSuite
  );
  const isLatestRunTestsJob =
    activeJobId !== null && activeJobId === runTestsJobId;
  const isConsoleDismissed =
    consoleDismissedJobId !== null && consoleDismissedJobId === runTestsJobId;
  const isEditorConsoleOpen =
    isLatestRunTestsJob &&
    !isConsoleDismissed &&
    (gateHasConsolePayload(requiredTestsGate) ||
      gateHasConsolePayload(generatedSuiteGate) ||
      gateHasConsolePayload(userSuiteGate));

  useEffect(() => {
    if (!legacyGeneratedSuite) {
      return;
    }
    if (!draft?.generatedTestsUrl) {
      return;
    }
    if (local.generatedTestSuite !== null) {
      return;
    }

    const controller = new AbortController();
    void (async () => {
      try {
        const resp = await fetch(draft.generatedTestsUrl!, {
          signal: controller.signal,
        });
        if (!resp.ok) {
          return;
        }
        const suite = await resp.text();
        if (typeof suite !== "string" || suite.trim().length === 0) {
          return;
        }
        // Read the latest draft timestamp at resolve time (via the ref) so an
        // in-flight fetch persists the CURRENT `lastUpdateTs`, not the one
        // captured when the fetch started. A stale write here would later be
        // misread as a cache-invalidating mismatch and discard a valid suite.
        commitGeneratedTestSuite(suite, draftRef.current?.lastUpdateTs ?? null);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    legacyGeneratedSuite,
    draft?.generatedTestsUrl,
    local.generatedTestSuite,
    commitGeneratedTestSuite,
  ]);

  useEffect(() => {
    if (!legacyGeneratedSuite) {
      return;
    }
    if (draft?.generatedTestsUrl) {
      clearGeneratedTestSuiteCache();
      return;
    }
    loadGeneratedTestSuiteFromCache();
  }, [
    legacyGeneratedSuite,
    draft?.generatedTestsUrl,
    clearGeneratedTestSuiteCache,
    loadGeneratedTestSuiteFromCache,
  ]);

  // Fetch the saved user test suite ONLY when the source URL changes — never on
  // every buffer keystroke. Both branches read/compare the latest `local` values
  // inside the immer updater (read-at-call-time), so editing the buffer can't
  // re-trigger the fetch and race-overwrite the user's in-flight edits. Mirrors
  // the generatedTestSuite fetch effect, which keys only on its URL.
  useEffect(() => {
    if (port.userTests.isLoading) {
      return;
    }
    const saved = port.userTests.data ?? null;
    setLocal((state) => {
      // Re-seed the baseline on every settle, but only follow it into the
      // buffer when the author has nothing unsaved. Dropping this guard is the
      // obvious "why is this convoluted" cleanup, and it discards in-progress
      // test edits with `isDirty` going false at the same moment, so nothing
      // warns.
      const wasDirty =
        (state.userTestSuite ?? state.savedUserTestSuite ?? "") !==
        (state.savedUserTestSuite ?? "");
      state.savedUserTestSuite = saved;
      if (!wasDirty) {
        state.userTestSuite = saved;
      }
    });
  }, [port.userTests.data, port.userTests.isLoading, setLocal]);

  // The server-persisted generated suite, for a domain that carries one on the
  // draft. It settles BEFORE any job runs, which is the whole point: the suite
  // an author sees after a reload is the one the publish pipeline will reuse,
  // rather than whatever this browser happened to cache.
  //
  // Same shape as the `userTests` settle above, and for the same reason: the
  // effect keys on the SERVER value only, re-seeds the dirty baseline on every
  // settle, and follows it into the buffer only when the author has nothing
  // unsaved. Keying on the buffer instead turned every keystroke into a
  // re-settle that wrote the server value back and reset the baseline, so the
  // edit vanished with `isDirty` false at the same moment.
  useEffect(() => {
    if (port.generatedTests.isLoading) {
      return;
    }
    const saved = port.generatedTests.data;
    if (saved === undefined || saved === null) {
      return;
    }
    seedGeneratedTestSuite(saved, draftRef.current?.lastUpdateTs ?? null);
  }, [
    port.generatedTests.data,
    port.generatedTests.isLoading,
    seedGeneratedTestSuite,
  ]);

  useEffect(() => {
    if (generatedTestSuiteFromJob === null) {
      return;
    }
    // Read-latest the draft timestamp via the ref so the persisted cache entry
    // is tagged with the CURRENT `lastUpdateTs` rather than a stale one captured
    // by this effect's closure when the job suite first arrived.
    seedGeneratedTestSuite(
      generatedTestSuiteFromJob,
      draftRef.current?.lastUpdateTs ?? null
    );
  }, [generatedTestSuiteFromJob, seedGeneratedTestSuite]);

  // When a publish job reaches the terminal `succeeded` status the active
  // version + version history have changed server-side. The port owns whatever
  // invalidation that implies; this only names the moment.
  //
  // Held in a ref because the port object is rebuilt every render: depending on
  // it directly would re-fire the invalidation on every render while a job sits
  // at `succeeded`, rather than once per transition.
  const onPublishSucceededRef = useRef(port.onPublishSucceeded);
  onPublishSucceededRef.current = port.onPublishSucceeded;
  useEffect(() => {
    if (jobStatus !== "succeeded") {
      return;
    }
    onPublishSucceededRef.current();
  }, [jobStatus, entityRef]);

  // While `isAwaitingGeneration` is true the port polls the draft. React
  // synchronously to the draft reaching a terminal status — stop polling and
  // surface the result.
  //
  // "ready": stop polling and null out `local.generatedTestSuite` so the
  // existing `draft?.generatedTestsUrl` effect above re-triggers and fetches
  // the new presigned URL.
  //
  // "failed": stop polling and surface the backend's error message.
  const aiTests = port.aiTests;
  const isAwaitingGeneration = aiTests?.isAwaitingGeneration ?? false;
  const setAwaitingGeneration = aiTests?.setAwaitingGeneration;
  useEffect(() => {
    if (!isAwaitingGeneration) {
      return;
    }

    if (draft?.generatedTestsStatus === "failed") {
      setAwaitingGeneration?.(false);
      toast.error(
        draft.generatedTestsError ??
          "Failed to generate AI tests. Please try again."
      );
      return;
    }

    if (draft?.generatedTestsStatus === "ready") {
      setAwaitingGeneration?.(false);
      // Null the cached suite so the draft.generatedTestsUrl effect fires and
      // fetches the newly written presigned URL.
      setLocal((state) => {
        state.generatedTestSuite = null;
      });
    }
  }, [
    isAwaitingGeneration,
    draft?.generatedTestsStatus,
    draft?.generatedTestsError,
    setLocal,
    setAwaitingGeneration,
  ]);

  // 10-minute timeout guard — stops polling if the backend never settles.
  useEffect(() => {
    if (!isAwaitingGeneration) {
      return;
    }
    const timeoutId = setTimeout(
      () => {
        setAwaitingGeneration?.(false);
        toast.error("AI test generation timed out. Please try again.");
      },
      10 * 60 * 1000
    );
    return () => clearTimeout(timeoutId);
  }, [isAwaitingGeneration, setAwaitingGeneration]);

  // True for the whole life of a gate job — publish runs and `Run tests` runs
  // alike. `isRunGatesInTransition` is load-bearing: between the runGates
  // mutation resolving and the first progress frame of the new job, the
  // mutation is no longer in flight and `jobStatus` still reports the
  // *previous* job's terminal status, so without it every action briefly
  // re-enables mid-run. It is the same window the gate rows are already held
  // open for, so the buttons and the rail now agree.
  const isPublishing =
    isContractStarting ||
    isRunGatesInTransition ||
    jobStatus === "queued" ||
    jobStatus === "running";

  useEffect(() => {
    if (!isRunGatesTransitioning) {
      return;
    }
    if (runGatesTransitionJobId === null) {
      return;
    }
    if (!port.publishJob.hasProgressFrame) {
      return;
    }
    if (port.publishJob.job?.id !== runGatesTransitionJobId) {
      return;
    }

    setRunGatesTransitionGates(null);
    setRunGatesTransitionJobId(null);
    setIsRunGatesTransitioning(false);
  }, [
    isRunGatesTransitioning,
    port.publishJob.hasProgressFrame,
    port.publishJob.job?.id,
    runGatesTransitionJobId,
    setIsRunGatesTransitioning,
    setRunGatesTransitionGates,
    setRunGatesTransitionJobId,
  ]);

  // Publish is permitted once every required VALIDATION gate of the most recent
  // run is passed. Before any run (all `pending`) publish is allowed to start
  // the job. Deploy-phase gates are excluded deliberately: they only run as the
  // tail of a publish attempt, so a failed deploy describes the last publish's
  // outcome rather than a precondition, and retrying the publish is the only
  // way to clear it — blocking on it strands the draft with no path forward.
  const deployGateKeys = new Set<string>(config.gates.deployGates);
  const hasFailedGate = gates.some(
    (g) =>
      !deployGateKeys.has(g.gateKey) &&
      (g.status === "failed" || g.status === "blocked")
  );
  // The single reason publish is blocked, evaluated in the order the UI reports
  // it (most actionable first). `canPublish` is derived from this so the two
  // can never disagree: `canPublish === (publishBlockReason === null)`.
  const publishBlockReason: PublishBlockReason | null = !hasEntityRef
    ? "unsaved_entity"
    : !editorState.syntax.isValid
      ? "syntax_invalid"
      : isDirty
        ? "unsaved_changes"
        : hasFailedGate
          ? "failed_gate"
          : isPublishing
            ? "publishing"
            : null;
  const canPublish = publishBlockReason === null;

  // --- handlers --------------------------------------------------------------
  const saveDraft = (callbacks?: {
    onSaved?: () => void;
    /** The save was refused before sending, or the server rejected it. */
    onNotSaved?: () => void;
  }) => {
    if (!entityRef || !editorState.syntax.isValid) {
      callbacks?.onNotSaved?.();
      return;
    }
    port.trackJob(null);
    // Only send the AI suite when the user actually edited it, so an untouched
    // version-fallback baseline is never promoted into the draft key. An emptied
    // buffer is still sent (as "") so the backend deletes the persisted suite.
    const generatedSuiteToPersist = generatedTestSuiteDirty
      ? (local.generatedTestSuite ?? "")
      : null;
    port.saveDraft(
      {
        files,
        userTestSuite,
        generatedTestSuite: generatedSuiteToPersist,
        dependencies,
        origin: sourceOrigin,
      },
      {
        onSuccess: () => {
          // Reconcile the dirty baseline to what we just persisted. The draft's
          // generated-tests URL effect is guarded against clobbering a non-null
          // buffer, so it would not otherwise re-sync `savedGeneratedTestSuite`.
          if (generatedSuiteToPersist !== null) {
            setLocal((state) => {
              state.savedGeneratedTestSuite = generatedSuiteToPersist;
            });
            // The server is now the source of truth; drop the pre-persist cache.
            clearGeneratedTestSuiteCache();
          }
          callbacks?.onSaved?.();
        },
        onError: () => callbacks?.onNotSaved?.(),
      }
    );
  };

  // --- test files ------------------------------------------------------------
  const addTest = () => {
    if (editorTests === null) {
      return;
    }
    const skeleton = editorTests.config.userSuiteSkeleton;
    setLocal((state) => {
      const current = state.userTestSuite ?? state.savedUserTestSuite ?? "";
      if (current.trim() === "") {
        state.userTestSuite = skeleton;
      }
    });
  };

  /**
   * "Edit as my test". The move is a server-side draft write that starts from
   * the PERSISTED suites, so pending edits are saved first — otherwise the
   * adopt would land on a user suite the editor no longer shows, and the next
   * save would write the unsaved buffer straight over the adopted test.
   */
  const adoptTest = (testId: string) => {
    if (editorTests === null || !entityRef) {
      return;
    }
    if (local.adoptingTestId !== null) {
      return;
    }
    if (isDirty && !editorState.syntax.isValid) {
      toast.error("Fix the errors in your code, then edit this test again.");
      return;
    }
    const userFilename = editorTests.config.files.userFilename;
    const settle = () =>
      setLocal((state) => {
        state.adoptingTestId = null;
      });
    setLocal((state) => {
      state.adoptingTestId = testId;
    });

    const adopt = () =>
      editorTests.inventory.adoptTests([testId], {
        onSuccess: (adopted) => {
          const moved = adopted.userTests.filter((test) =>
            adopted.adoptedTestIds.includes(test.id)
          );
          setLocal((state) => {
            // Persisted by the adopt itself, so it is the baseline too: the
            // author lands on their file with nothing unsaved.
            state.userTestSuite = adopted.userTestSuite;
            state.savedUserTestSuite = adopted.userTestSuite;
            state.activeFile = userFilename;
            state.reveal =
              moved.length > 0
                ? {
                    file: userFilename,
                    startLine: Math.min(...moved.map((test) => test.startLine)),
                    endLine: Math.max(...moved.map((test) => test.endLine)),
                  }
                : null;
            state.adoptingTestId = null;
          });
        },
        onError: settle,
      });

    if (isDirty) {
      saveDraft({ onSaved: adopt, onNotSaved: settle });
    } else {
      adopt();
    }
  };

  // The persisted user suite moved after the judged run started — a save, an
  // adoption — so the run's verdicts describe a file that no longer exists,
  // even though the buffer is clean. The generated suite is the run's own
  // artifact (the pipeline writes it back), so it is never stale this way.
  const judgedRunStartedAtTs = port.publishJob.job?.startedAtTs ?? null;
  const userSuiteNewerThanRun =
    judgedRunStartedAtTs !== null &&
    typeof draft?.lastUpdateTs === "number" &&
    draft.lastUpdateTs > judgedRunStartedAtTs;

  // The same suite gates the console drawer reads.
  const testFiles: WorkspaceEditorTestFiles | null =
    editorTests === null
      ? null
      : {
          user: {
            name: editorTests.config.files.userFilename,
            isEmpty: userTestSuite.trim() === "",
            addTest,
            // An unsaved buffer is not what the last run ran, so its lines
            // would carry another text's verdicts; nor is a suite persisted
            // since the run started.
            verdicts:
              userTestSuiteDirty || userSuiteNewerThanRun
                ? []
                : suiteGateVerdicts(
                    userSuiteGate,
                    scanSuiteTestLocations(userTestSuite)
                  ),
          },
          generated: {
            name: editorTests.config.files.generatedFilename,
            // A failed read is not an empty suite: "generated on your next
            // run" would send the author to run tests for a file that exists.
            status: editorTests.inventory.generated.isLoading
              ? "loading"
              : editorTests.inventory.generated.isError
                ? "error"
                : generatedSuiteSource === null ||
                    generatedSuiteSource.trim() === ""
                  ? "empty"
                  : "ready",
            tests: generatedInventory?.tests ?? [],
            verdicts: suiteGateVerdicts(
              generatedSuiteGate,
              inventoryLocations(generatedInventory?.tests ?? [])
            ),
            adoptTest,
            adoptingTestId: local.adoptingTestId,
          },
          active: activeTestFile,
        };

  // --- one-shot buffer flush on entity persistence ---------------------------
  // A brand-new parent record has no id, so every mutation is inert and the
  // editor + controls live only in local state. The moment the parent is saved
  // and an id materializes, persist whatever the user already wrote as the first
  // draft so their work isn't stranded. Scoped to the undefined->string
  // transition (not merely "id present") so editing an already-saved entity
  // never turns into autosave-on-keystroke. For a domain whose ref is a
  // constant, this one-shot naturally no-ops — which is correct.
  const prevEntityRefRef = useRef(entityRef);
  const pendingFlushRef = useRef(false);
  useEffect(() => {
    const appeared = !prevEntityRefRef.current && !!entityRef;
    prevEntityRefRef.current = entityRef;
    if (appeared) {
      pendingFlushRef.current = local.filesOverride !== null || controlsDirty;
    }
  }, [entityRef, local.filesOverride, controlsDirty]);
  useEffect(() => {
    if (!pendingFlushRef.current) {
      return;
    }
    // Wait for the now-enabled entity read to settle and only flush when there
    // is no pre-existing server draft to clobber, the source is valid, and no
    // manual save is already in flight.
    if (!entityRef || port.isLoading || draft !== null) {
      return;
    }
    if (!editorState.syntax.isValid || port.isSavingDraft) {
      return;
    }
    pendingFlushRef.current = false;
    saveDraft();
  }, [
    entityRef,
    port.isLoading,
    draft,
    editorState.syntax.isValid,
    port.isSavingDraft,
  ]);

  /**
   * Starts a publish run against what is *persisted*.
   *
   * `savedUserTestSuite` is a parameter rather than a closure read because the
   * save-then-deploy path publishes the suite it has just written, which the
   * draft query has not echoed back yet — reading the closure there would
   * publish the previous save's tests.
   */
  const startPublish = (
    userSuiteToRun: string,
    notes?: string,
    title?: string
  ) => {
    if (!entityRef) {
      return;
    }
    // A new publish run should start from a clean publish-gate surface, so
    // clear the currently-tracked job before entering the request cycle.
    port.trackJob(null);
    setRunTestsJobId(null);
    setConsoleDismissedJobId(null);
    port.publish(
      {
        generatedTestSuite: local.generatedTestSuite,
        savedUserTestSuite: userSuiteToRun,
        skipAiTests: local.skipAiTests,
        notes,
        title,
      },
      {
        onSuccess: (data) => {
          port.trackJob(data.publishJobId);
          setPublishModalOpen(false);
        },
      }
    );
  };

  const publishDraft = (notes?: string, title?: string) =>
    startPublish(savedUserTestSuite, notes, title);

  /**
   * Save is a deploy.
   *
   * A rule has no draft state to sit in: the version a save writes is the
   * version that serves, so persisting and running the publish gates is one
   * action rather than two the author has to remember to sequence. The gates
   * are what make that safe — a failing gate fails the publish, and the saved
   * source stays unserved.
   */
  const saveAndPublish = (notes?: string, title?: string) => {
    saveDraft({ onSaved: () => startPublish(userTestSuite, notes, title) });
  };

  const generateTests = () => {
    if (!entityRef || !aiTests) {
      return;
    }
    aiTests.generateTests({
      onSuccess: () => {
        // Kickoff was accepted — start polling the draft until
        // `generatedTestsStatus` settles.
        aiTests.setAwaitingGeneration(true);
      },
    });
  };

  const runTests = () => {
    if (!entityRef || !port.gateRun) {
      return;
    }
    // Preserve current gate rows through the publish-job handoff so we do not
    // flash all gates back to canonical-pending while waiting for the new
    // job snapshot.
    setRunGatesTransitionGates(gates);
    setRunGatesTransitionJobId(null);
    setIsRunGatesTransitioning(true);
    setRunTestsJobId(null);
    setConsoleDismissedJobId(null);
    port.gateRun.runGates(
      {
        generatedTestSuite: local.generatedTestSuite,
        savedUserTestSuite,
        skipAiTests: local.skipAiTests,
      },
      {
        onSuccess: (data) => {
          port.trackJob(data.publishJobId);
          setRunGatesTransitionJobId(data.publishJobId);
          // Mark this as a test-run job (drives the console drawer) and clear
          // any prior dismissal so the new run re-opens the drawer.
          setRunTestsJobId(data.publishJobId);
          setConsoleDismissedJobId(null);
        },
        onError: () => {
          setRunGatesTransitionGates(null);
          setRunGatesTransitionJobId(null);
          setIsRunGatesTransitioning(false);
        },
      }
    );
  };

  const rollbackToVersion = (version: number) => {
    if (!entityRef) {
      return;
    }
    port.rollbackToVersion(version, {
      onSuccess: () => setRollbackTargetVersion(null),
    });
  };

  const unpublishEntity = () => {
    if (!entityRef || !activeVersion || !port.unpublish) {
      return;
    }
    // Only reachable when the domain supplied the `unpublish` capability
    // group, so the copy is normally present. The default keeps a domain that
    // supplied the group without the copy showing a real prompt rather than
    // the string "undefined" — never silently skipping the confirmation.
    if (
      !window.confirm(
        config.chrome.confirmUnpublish ?? "Unpublish the active version?"
      )
    ) {
      return;
    }
    port.unpublish.unpublish();
  };

  const deleteVersion = (version: number) => {
    if (!entityRef) {
      return;
    }
    port.deleteVersion(version);
  };

  const importVersionAsDraft = (version: number) => {
    if (!entityRef || !port.versionImport) {
      return;
    }
    // Same contract as confirmUnpublish: guarded by the `versionImport`
    // capability group above, defaulted so a missing string cannot turn into
    // an "undefined" prompt.
    if (
      isDirty &&
      !window.confirm(
        config.chrome.confirmDirtyImport ??
          "Replace your unsaved changes with this version?"
      )
    ) {
      return;
    }

    port.versionImport.importVersion(
      version,
      async (
        importedDraft: WorkspaceImportedDraft
      ): Promise<WorkspaceImportedArtifacts> => {
        // Inline first, URL second — see WorkspaceImportedDraft. A domain that
        // inlines its code has already delivered it in the mutation response,
        // so there is nothing to fetch and no window in which the editor could
        // reseed from a half-loaded import.
        const [source, userTests, generatedTests] = await Promise.all([
          importedDraft.source !== undefined
            ? importedDraft.source
            : fetchTextArtifact(importedDraft.sourceUrl),
          importedDraft.userTests !== undefined
            ? importedDraft.userTests
            : fetchTextArtifact(importedDraft.userTestsUrl),
          fetchTextArtifact(importedDraft.generatedTestsUrl),
        ]);
        return { source, userTests, generatedTests };
      },
      (importedDraft, artifacts) => {
        setLocal((state) => {
          state.filesOverride = null;
          state.activeFile = entryFilename;
          state.savedUserTestSuite = artifacts.userTests;
          state.userTestSuite = artifacts.userTests;
          state.generatedTestSuite = artifacts.generatedTests;
          state.savedGeneratedTestSuite = artifacts.generatedTests;
        });
        if (artifacts.generatedTests) {
          persistGeneratedTestSuite(
            artifacts.generatedTests,
            importedDraft.lastUpdateTs ?? null
          );
        } else {
          clearGeneratedTestSuiteCache();
        }
      }
    );
  };

  const writeTestBuffer = (value: string) => {
    if (activeTestFile !== "user") {
      return;
    }
    setLocal((state) => {
      state.userTestSuite = value;
    });
  };

  return {
    entityRef,
    entityName: port.entity.name,
    entityStatusLabel: port.entity.statusLabel,

    isLoading: port.isLoading,
    isEmpty,
    draft,
    activeVersion,
    versions: port.versions,
    availableSecretNames: port.availableSecretNames,
    secrets: port.secrets,

    editor: { ...editorState, testFiles },
    // Writes the live buffer into the active editable file (no-op on the
    // readonly contract tab and the generated suite). On the user test file it
    // is the user suite, which records no source provenance.
    setEditorValue: (value) => {
      if (activeTestFile !== null) {
        writeTestBuffer(value);
        return;
      }
      setFileValue(activeFile, value);
      setSourceOrigin("manual");
    },
    // Same write, but records the buffer as AI-chat output so the next draft
    // save persists `origin: "chat"` — the provenance version history renders.
    applyAiSource: (value) => {
      if (activeTestFile !== null) {
        writeTestBuffer(value);
        return;
      }
      setFileValue(activeFile, value);
      setSourceOrigin("chat");
    },
    editorConsole: {
      isOpen: isEditorConsoleOpen,
      requiredLogs: gateConsoleLogs(requiredTestsGate),
      generatedLogs: gateConsoleLogs(generatedSuiteGate),
      userLogs: gateConsoleLogs(userSuiteGate),
      close: () => setConsoleDismissedJobId(runTestsJobId),
    },

    dependencies,
    addDependency: port.controls.addDependency,
    removeDependency: port.controls.removeDependency,
    updateDependencyVersion: port.controls.updateDependencyVersion,
    isDirty,
    draftSnapshot: {
      files,
      userTestSuite,
      generatedTestSuite: generatedTestSuiteDirty
        ? (local.generatedTestSuite ?? "")
        : null,
      dependencies,
      origin: sourceOrigin,
    },

    generatedTestSuite: local.generatedTestSuite,
    // Edits only the live buffer; the diff against `savedGeneratedTestSuite`
    // drives the dirty state and what `saveDraft` persists.
    setGeneratedTestSuite: (value) =>
      setLocal((state) => {
        state.generatedTestSuite = value;
      }),
    userTestSuite,
    setUserTestSuite: (value) =>
      setLocal((state) => {
        state.userTestSuite = value;
      }),
    isGeneratingTests:
      (aiTests?.isGeneratingTests ?? false) || isAwaitingGeneration,
    skipAiTests: local.skipAiTests,
    setSkipAiTests: (value) =>
      setLocal((state) => {
        state.skipAiTests = value;
      }),

    selectedTab,
    setSelectedTab,

    publish: {
      jobId: activeJobId,
      job: port.publishJob.job,
      gates,
      isPublishing,
      unpassedGateCount,
      canPublish,
      publishBlockReason,
      /**
       * Follow a job the CALLER started. A domain can deploy from outside the
       * workspace's own Save — Agentic Logic's display settings do, since
       * saving them is what deploys them — and the rail must show that job
       * rather than the last one this shell happened to start.
       */
      trackJob: (jobId: string) => port.trackJob(jobId),
    },

    isPublishModalOpen,
    openPublishModal: () => setPublishModalOpen(true),
    closePublishModal: () => setPublishModalOpen(false),
    rollbackTargetVersion,
    openRollbackModal: (version) => setRollbackTargetVersion(version),
    closeRollbackModal: () => setRollbackTargetVersion(null),
    isSecretsModalOpen,
    openSecretsModal: () => setSecretsModalOpen(true),
    closeSecretsModal: () => setSecretsModalOpen(false),

    isSavingDraft: port.isSavingDraft,
    isRollingBack: port.isRollingBack,
    isUnpublishing: port.unpublish?.isUnpublishing ?? false,
    isDeletingVersion: port.isDeletingVersion,
    isImportingVersionDraft: port.versionImport?.isImportingVersion ?? false,
    isMutatingSecret: port.isMutatingSecret,

    saveDraft,
    publishDraft,
    saveAndPublish,
    generateTests,
    runTests,
    rollbackToVersion,
    unpublishEntity,
    deleteVersion,
    importVersionAsDraft,
    createSecret: port.createSecret,
    deleteSecret: port.deleteSecret,
  };
};
